import { BaseButton } from '@/components/button/BaseButton'
import { LinkButton } from '@/components/button/LinkButton'
import { css, Theme } from '@emotion/react'
import { POST } from '@/api/api'
import { useAtom, useSetAtom } from 'jotai'
import { authAtom } from '@/store/auth/authAtom'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ROLE } from '@/constants/roles'
import { sidebarCollapseAtom } from '@/store/sidebarCollapse'

const sidebarStyle = (t: Theme) => css`
  background-color: ${t.c.ec.accentBlue};
  color: ${t.c.ec.white};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  padding: 1rem;
`

const sidebarExpandedStyle = css`
  width: 20rem;
`
const sidebarCollapsedStyle = css`
  width: 7rem;
`

const logoContainerStyle = css`
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  height: 8rem;
`

const logoStyle = css`
  margin: auto;
`

const expandedLogoStyle = css`
  width: 4rem;
  height: 4rem;
  margin: auto;
`

const collapsedLogoStyle = css`
  width: 3rem;
  height: 3rem;
`

const menuStyle = css`
  font-size: 1.3rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`

const menuItemStyle = (t: Theme) => css`
  background-color: ${t.c.ec.accentBlue};
  padding: 1.2rem 1.6rem;
  height: 4rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  border: none;
  justify-content: start;
  &:hover {
    background-color: ${t.c.ec.iconBlue}!important;
  }
`
const buttonExpandedStyle = css`
  width: 18rem;
`
const buttonCollapsedStyle = css`
  width: 5rem;
`

const menuIconContainerStyle = css`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

const toggleButtonStyle = (t: Theme) => css`
  height: 4rem;
  border-radius: 0.5rem;
  border: none;
  display: flex;
  align-items: center;
  justify-content: end;
  background-color: ${t.c.ec.accentBlue};
  :hover {
    background-color: ${t.c.ec.iconBlue}!important;
  }
  :after {
    content: '';
    right: 0.8rem;
    width: 0.8rem;
    height: 0.8rem;
    border-bottom: 0.1rem solid ${t.c.ec.white};
    border-left: 0.1rem solid ${t.c.ec.white};
    transform-origin: center;
  }
`

const toggleButtonExpandedStyle = css`
  :after {
    transform: rotate(45deg);
  }
`
const toggleButtonCollapsedStyle = css`
  :after {
    transform: rotate(225deg);
  }
`
const menuItems = [
  { path: '/health-insurers', label: '保険者一覧', icon: "/images/health-insurers-icon.svg" },
  // {
  //   path: '/medical-subsidies',
  //   label: '医療助成一覧',
  //   icon: "/images/medical-subsidies-icon.svg",
  // },
  { path: '/destinations', label: '送達先一覧', icon: "/images/destinations-icon.svg" },
  {
    path: '/users',
    label: 'アカウント一覧',
    icon: "/images/accounts-icon.svg",
    requiredRole: ROLE.ADMIN,
  },
]

export const SideBar = () => {
  const [user, setUser] = useAtom(authAtom)
  const [isDisabled, setIsDisabled] = useState(false)
  const setIsCollapsed = useSetAtom(sidebarCollapseAtom)
  const [isCollapsed] = useAtom(sidebarCollapseAtom)
  const pathname = useLocation().pathname

  const handleSideBarToggle = () => {
    setIsCollapsed(!isCollapsed)
  }
  const handleLogout = () => {
    setIsDisabled(true)
    POST('/auth/logout')
      .then(() => {
        window.location.href = '/auth/login'
      })
      .catch(() => {
        window.location.href = '/auth/login'
      })
      .finally(() => {
        setIsDisabled(false)
      })
  }

  return (
    <aside
      css={[sidebarStyle, isCollapsed ? sidebarCollapsedStyle : sidebarExpandedStyle]}
    >
      <div>
        <div css={logoContainerStyle}>
          <img
            css={[logoStyle, isCollapsed ? collapsedLogoStyle : expandedLogoStyle]}
            src="/images/receipt-master-logo-blue.svg"
            alt="Master Maintenance"
          />
        </div>
        <nav css={menuStyle}>
        <ul>
        {menuItems
          .filter(
            (item) => !item.requiredRole || user?.role === item.requiredRole,
          )
          .map((item) => (
            <li key={item.path}>
              <LinkButton
                to={item.path}
                aria-label={item.label}
                aria-current={pathname === item.path ? 'page' : undefined}
                title={isCollapsed ? undefined : item.label}
                addStyle={[
                  menuItemStyle,
                  isCollapsed ? buttonCollapsedStyle : buttonExpandedStyle,
                ]}
              >
                <div css={menuIconContainerStyle}>
                  <img src={item.icon} alt={item.label} />
                </div>
                {!isCollapsed && <span>{item.label}</span>}
              </LinkButton>
            </li>
          ))}
          </ul>
        </nav>
      </div>
      <div>
        <BaseButton
          addStyle={[
            menuItemStyle,
            isCollapsed ? buttonCollapsedStyle : buttonExpandedStyle,
          ]}
          onClick={handleLogout}
          disabled={isDisabled}
        >
          <div css={menuIconContainerStyle}>
            <img src="/images/logout-icon.svg" alt="ログアウト" />
          </div>
          {!isCollapsed && <span>ログアウト</span>}
        </BaseButton>
        <BaseButton
          addStyle={[
            toggleButtonStyle,
            isCollapsed ? buttonCollapsedStyle : buttonExpandedStyle,
            isCollapsed ? toggleButtonCollapsedStyle : toggleButtonExpandedStyle,
          ]}
          onClick={handleSideBarToggle}
          aria-label={isCollapsed ? "サイドバーを展開" : "サイドバーを折りたたむ"}
        ></BaseButton>
      </div>
    </aside>
  )
}
